'use client';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from '../store';
import { logout, TAB_SYNC_KEY } from '../features/auth/slices/authSlice';
import axios from 'axios';
import { usePathname, useRouter } from 'next/navigation';
import useProactiveTokenRefresh from '../hooks/useProactiveTokenRefresh';

interface AuthContextType {
  user: AuthState;
  isAuthenticated: boolean;
  handleLogout: ({ saveUrl }: { saveUrl?: boolean }) => void;
  isLogginInPRogress: React.MutableRefObject<boolean>;
  isLoggingOut: React.MutableRefObject<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isLogginInPRogress = useRef(false);
  const isLoggingOut = useRef(false);
  const dispatch = useDispatch();
  const authState = useSelector((state: RootState) => state.auth) as AuthState;
  const router = useRouter();
  const pathName = usePathname();
  const isAuthenticated = Boolean(authState && authState.token);
  useProactiveTokenRefresh(authState?.token);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const storedSyncId = localStorage.getItem(TAB_SYNC_KEY);
      const memorySyncId = authState.tabSyncId;

      if (storedSyncId && memorySyncId && storedSyncId !== memorySyncId) {
        window.location.reload();
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [authState.tabSyncId]);

  const handleLogout = async ({
    saveUrl = false,
  }: {
    saveUrl?: boolean;
  }): Promise<void> => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    const returnUrl = saveUrl
      ? `/account/login${pathName !== '/' ? `?returnUrl=${pathName}` : ''}`
      : '/account/login';
    await axios.post('/api/auth/logout');
    if (isAuthenticated) dispatch(logout());
    router.push(returnUrl);
    isLoggingOut.current = false;
  };

  const value = useMemo(
    () => ({
      user: authState,
      isAuthenticated,
      handleLogout,
      isLogginInPRogress,
      isLoggingOut,
    }),
    [authState, isAuthenticated, store],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
